const crypto = require('crypto');

const RSA_PUBLIC_KEY_PEM = `-----BEGIN RSA PUBLIC KEY-----
MIIBCgKCAQEA4+dvvY7uM+opc+nLG/DumSeFo7aVcIm1LO+mUIrWprRzP0a1GpEQ
4qtO9MRf/lwEIx38+4BZ4XOGzevVtuUvfIOUE7ATtQW7FKJf5PnSLCI6/k0vl1FC
LUSVmEP6qPJrIjZ4zXoqi3yuNZ+1odbRA//GHgcgSyyyaLqzwjkpWGXouMYmvYsS
ppk/fv2EWABstPNxWa4EOBCadTUPkXNQ76NBEP9xzfJS20wiDv1oS/vKvrSUpWcJ
nazkkBvqQbbAqVbQFTF/DPikppu6ZT6lGIXvJKCreeFiHA2qK6tS18SWVHW9ARz1
+pjB1eqIIYtohWpT2B40OC4O8tVeBAnbiQIDAQAB
-----END RSA PUBLIC KEY-----`;

function normalizeRuntime(runtime = {}) {
  return {
    nowSeconds: typeof runtime.nowSeconds === 'function'
      ? runtime.nowSeconds
      : () => Math.trunc(Date.now() / 1000),
    randomBytes: typeof runtime.randomBytes === 'function'
      ? runtime.randomBytes
      : (size) => crypto.randomBytes(size),
    rsaEncryptBase64: typeof runtime.rsaEncryptBase64 === 'function'
      ? runtime.rsaEncryptBase64
      : (plaintext, publicKeyPem) => rsaEncryptBase64(plaintext, publicKeyPem),
  };
}

function hexToBytes(hexStr) {
  let normalized = String(hexStr).trim();
  if (normalized.toLowerCase().startsWith('0x')) {
    normalized = normalized.slice(2);
  }
  return Buffer.from(normalized, 'hex');
}

function importAesCbcKey(raw) {
  const key = Buffer.from(raw);
  if (![16, 24, 32].includes(key.length)) {
    throw new Error('AES key must be 16/24/32 bytes for 128/192/256-bit');
  }
  return key;
}

function bytesToB64(data) {
  return Buffer.from(data).toString('base64');
}

function aesCbcEncrypt(plainText, keyHex, ivBytes) {
  const key = importAesCbcKey(hexToBytes(keyHex));
  const iv = Buffer.from(ivBytes);
  if (iv.length !== 16) {
    throw new Error('AES-CBC IV must be exactly 16 bytes');
  }

  const cipher = crypto.createCipheriv(`aes-${key.length * 8}-cbc`, key, iv);
  const ctBytes = Buffer.concat([
    cipher.update(Buffer.from(String(plainText), 'utf8')),
    cipher.final(),
  ]);
  const ivPlusCt = Buffer.concat([iv, ctBytes]);

  return {
    cipherText: bytesToB64(ivPlusCt),
    encryptedData: bytesToB64(ctBytes),
    iv: bytesToB64(iv),
  };
}

function bytesToHex(data) {
  return Buffer.from(data).toString('hex');
}

function rsaEncryptBase64(plaintext, publicKeyPem) {
  const encrypted = crypto.publicEncrypt(
    {
      key: publicKeyPem,
      padding: crypto.constants.RSA_PKCS1_PADDING,
    },
    Buffer.from(String(plaintext), 'utf8'),
  );
  return bytesToB64(encrypted);
}

function aesCbcEncryptWithRandomIv(plaintext, keyHex, runtimeOptions) {
  const runtime = normalizeRuntime(runtimeOptions);
  const iv = Buffer.from(runtime.randomBytes(16));
  if (iv.length !== 16) {
    throw new Error('Random IV must be exactly 16 bytes');
  }
  return aesCbcEncrypt(plaintext, keyHex, iv);
}

function encryptWithRsaPublicKeyBase64(plaintext, runtimeOptions) {
  const runtime = normalizeRuntime(runtimeOptions);
  const result = runtime.rsaEncryptBase64(plaintext, RSA_PUBLIC_KEY_PEM);
  if (!result) {
    throw new Error('RSA encrypt failed');
  }
  return result;
}

function dtraitToken(path, runtimeOptions) {
  const runtime = normalizeRuntime(runtimeOptions);
  const payload = {
    dtrait: 'IAAAAAAA',
    timestamp: Math.trunc(runtime.nowSeconds()),
    sdkVersion: '1.0.31',
    path: String(path),
  };
  const jsonText = JSON.stringify(payload);

  const randKeyBytes = Buffer.from(runtime.randomBytes(16));
  if (randKeyBytes.length !== 16) {
    throw new Error('Random AES key must be exactly 16 bytes');
  }
  const keyHex = bytesToHex(randKeyBytes);

  const enc = aesCbcEncryptWithRandomIv(jsonText, keyHex, runtime);
  const rsaB64 = encryptWithRsaPublicKeyBase64(keyHex, runtime);
  return `d0_${rsaB64}_${enc.cipherText}`;
}

function generateDtrait(path, runtimeOptions) {
  return dtraitToken(path, runtimeOptions);
}

module.exports = {
  RSA_PUBLIC_KEY_PEM,
  normalizeRuntime,
  hexToBytes,
  hex_to_bytes: hexToBytes,
  importAesCbcKey,
  import_aes_cbc_key: importAesCbcKey,
  bytesToB64,
  bytes_to_b64: bytesToB64,
  aesCbcEncrypt,
  aes_cbc_encrypt: aesCbcEncrypt,
  bytesToHex,
  bytes_to_hex: bytesToHex,
  rsaEncryptBase64,
  rsa_encrypt_base64: rsaEncryptBase64,
  aesCbcEncryptWithRandomIv,
  aes_cbc_encrypt_with_random_iv: aesCbcEncryptWithRandomIv,
  encryptWithRsaPublicKeyBase64,
  encrypt_with_rsa_public_key_base64: encryptWithRsaPublicKeyBase64,
  dtraitToken,
  dtrait_token: dtraitToken,
  generateDtrait,
  generate_dtrait: generateDtrait,
};
